/**
 * PwaUpdatePrompt
 * ----------------
 * Shows a small, dismissible "a new version is available" prompt whenever a new
 * build of this PWA has been deployed, and applies it when the user taps Update.
 *
 * IMPORTANT — this only controls the app-shell / service-worker update lifecycle.
 * It does NOT touch authentication, tokens, cookies, or any stored session.
 * Applying an update just activates the already-downloaded new build and reloads
 * the page — exactly like a normal browser refresh — so the httpOnly refresh
 * cookie and the persisted access token survive and the user stays signed in.
 *
 * How it works:
 *   - registerType is 'prompt' (see vite.config.js), so a freshly deployed
 *     service worker installs and then WAITS instead of activating silently.
 *   - useRegisterSW surfaces that waiting worker as `needRefresh`, which opens
 *     the snackbar below.
 *   - updateServiceWorker(true) messages the waiting worker to skipWaiting and
 *     reloads the page once it takes control.
 *   - We also poll registration.update() periodically and on focus/online so
 *     long-lived tabs and installed (standalone) sessions notice new deploys.
 */

import { useRegisterSW } from 'virtual:pwa-register/react';
import { Snackbar, Alert, Button, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

// How often to ask the browser to check the server for a newer build.
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

export default function PwaUpdatePrompt() {
  const { t } = useTranslation();

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      if (!registration) return;

      const check = () => {
        // Only meaningful when online; errors (offline, transient) are ignored.
        if (navigator.onLine === false) return;
        registration.update().catch(() => {});
      };

      // Periodic check for long-open sessions.
      setInterval(check, UPDATE_CHECK_INTERVAL_MS);

      // Check when the app regains focus or the network comes back — this is
      // when a returning user is most likely to be on an outdated build.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
      window.addEventListener('online', check);
    },
  });

  const dismiss = () => setNeedRefresh(false);
  const apply = () => updateServiceWorker(true); // skipWaiting + reload

  return (
    <Snackbar
      open={needRefresh}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ zIndex: (theme) => theme.zIndex.snackbar + 1 }}
    >
      <Alert
        severity="info"
        variant="filled"
        icon={false}
        sx={{ width: '100%', alignItems: 'center' }}
        action={
          <Box sx={{ display: 'flex', gap: 1, whiteSpace: 'nowrap' }}>
            <Button color="inherit" size="small" onClick={dismiss}>
              {t('pwaUpdate.later', 'Later')}
            </Button>
            <Button color="inherit" size="small" variant="outlined" onClick={apply}>
              {t('pwaUpdate.update', 'Update')}
            </Button>
          </Box>
        }
      >
        {t('pwaUpdate.message', 'A new version is available.')}
      </Alert>
    </Snackbar>
  );
}
